// src/components/admin/AdminDashboardLayout.tsx
import React, { useState } from "react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useTheme } from "../../config/theme.config";
import {
    faUsers,
    faCog,
    faSignOutAlt,
    faUserCircle,
    faBell,
    faChartBar,
    faClipboardList,
    faChevronLeft,
    faBars,
    faTimes,
    faTruckFast,
    faTruck,
    faUserShield,
    faShieldAlt,
    faFileExcel,
    faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../styles/app/DashboardLayout.css"; // Using DashboardLayout styles
import Footer from "../app/Footer";
import AdminNav from "./AdminNav";
import TopBar from "../common/TopBar";
import { permissionCheckerService } from "../../services/permission.checker.service";
import elPaketLogoIMG from "../../assets/images/logo-elpaket.png";

interface AdminDashboardLayoutProps {
    children: React.ReactNode;
}

interface NavItem {
    path?: string;
    icon: any;
    name: string;
    module?: string;
    action?: string;
    isGroup?: boolean;
    children?: NavItem[];
}

const AdminDashboardLayout: React.FC<AdminDashboardLayoutProps> = ({
    children,
}) => {
    const { adminData, logoutAdmin } = useAdminAuth();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem("adminSidebarCollapsed");
        return saved ? JSON.parse(saved) : false;
    });
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false);
    const [isReportsOpen, setIsReportsOpen] = useState(false);
    const [isDispatchersOpen, setIsDispatchersOpen] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        await logoutAdmin();
        navigate("/admin/login", { replace: true });
    };

    const getGreetingName = () => {
        if (adminData?.FirstName && adminData?.LastName)
            return adminData.FirstName + " " + adminData.LastName;
        return "Administrador";
    };

    const navItems: NavItem[] = [
        {
            path: "/admin/dashboard",
            icon: faChartBar,
            name: "Dashboard",
        },
        {
            path: "/admin/customers",
            icon: faUserShield,
            name: "Clientes",
            module: "CUSTOMERS",
            action: "READ",
        },
        {
            path: "/admin/orders",
            icon: faClipboardList,
            name: "Órdenes",
            module: "ORDERS",
            action: "READ",
        },
        {
            path: "/admin/courier-services",
            icon: faTruck,
            name: "Couriers",
            module: "CARRIERSERVICES",
            action: "READ",
        },
        {
            name: "Afiliados",
            icon: faTruckFast,
            isGroup: true,
            children: [
                {
                    path: "/admin/dispatchers",
                    icon: faTruckFast,
                    name: "Transportistas",
                    module: "DISPATCHERS",
                    action: "READ",
                },
                {
                    path: "/admin/zipcode-dispatch-zones",
                    icon: faGlobe,
                    name: "Zonas de Despacho",
                    module: "ZIPCODE DISPATCH ZONE",
                    action: "READ",
                }
            ]
        },
        {
            name: "Reportes",
            icon: faFileExcel,
            isGroup: true,
            children: [
                {
                    path: "/admin/rates-report",
                    icon: faFileExcel,
                    name: "Reporte Tarifas",
                    module: "REPORTS",
                    action: "RATES",
                }
            ]
        },
        {
            name: "Gestión de Usuarios",
            icon: faUsers,
            isGroup: true,
            children: [
                {
                    path: "/admin/users",
                    icon: faUsers,
                    name: "Usuarios",
                    module: "USERS",
                    action: "READ",
                },
                {
                    path: "/admin/roles",
                    icon: faShieldAlt,
                    name: "Roles",
                    module: "ROLES",
                    action: "READ",
                },
            ]
        },
        {
            path: "",
            icon: faSignOutAlt,
            name: "Salir",
        },
    ];

    const visibleNavItems = navItems.reduce((acc: NavItem[], item) => {
        if (item.isGroup && item.children) {
            const visibleChildren = item.children.filter(child =>
                !child.module || !child.action ||
                permissionCheckerService.checkPermission(adminData, child.module, child.action)
            );
            if (visibleChildren.length > 0) {
                acc.push({ ...item, children: visibleChildren });
            }
        } else if (
            (!item.module || !item.action) ||
            permissionCheckerService.checkPermission(adminData, item.module, item.action)
        ) {
            acc.push(item);
        }
        return acc;
    }, []);

    const brandName = process.env.REACT_APP_BRAND_NAME;

    return (
        <>
            <TopBar hideSocial={true} />
            <div className="dashboard-nav-wrapper">
                <AdminNav />
            </div>

            {/* Mobile Nav Header */}
            <header className="dashboard-mobile-nav">
                <div className="mobile-nav-content">
                    <div
                        className="mobile-nav-left"
                        onClick={() => navigate("/")}
                        style={{ cursor: "pointer" }}
                    >
                        <img 
                            src={elPaketLogoIMG} 
                            alt="elPaket" 
                            className="nav-logo-img" 
                            style={{ height: "44px", objectFit: "contain" }}
                        />
                    </div>
                    <button
                        className="mobile-hamburger"
                        onClick={() => {
                            setIsMobileOpen(!isMobileOpen);
                            if (!isMobileOpen) {
                                setIsCollapsed(false);
                                localStorage.setItem("adminSidebarCollapsed", "false");
                            }
                        }}
                        aria-label="Toggle sidebar"
                    >
                        <FontAwesomeIcon icon={isMobileOpen ? faTimes : faBars} />
                    </button>
                </div>
            </header>

            <div className="dashboard-layout">
                <aside
                    className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""
                        }`}
                >
                    <div
                        className="sidebar-toggle-btn"
                        onClick={() => {
                            const nextVal = !isCollapsed;
                            setIsCollapsed(nextVal);
                            localStorage.setItem("adminSidebarCollapsed", JSON.stringify(nextVal));
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faChevronLeft}
                            className={isCollapsed ? "icon-collapsed" : ""}
                        />
                    </div>

                    <div className="dashboard-layout-sidebar-user-box">
                        <div className="user-box-header"></div>
                        <div className="user-box-icon">
                            <FontAwesomeIcon icon={faUserCircle} />
                        </div>
                        <div className="user-box-body">
                            <h3>{getGreetingName()}</h3>
                            <p>{adminData?.Email}</p>
                            {adminData?.Roles && adminData.Roles.length > 0 && (
                                <div className="user-box-roles">
                                    {adminData.Roles.map((role, idx) => (
                                        <span
                                            key={role.Id || idx}
                                            className={`user-role-badge ${role.IsSuperAdmin || (role as any).isSuperAdmin ? "super-admin" : ""}`}
                                        >
                                            {role.RoleName}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <ul className="sidebar-nav">
                        {visibleNavItems.map((item) => (
                            <li key={item.name}>
                                 {item.isGroup ? (
                                    (() => {
                                        const isGroupOpen = 
                                            item.name === "Reportes" ? isReportsOpen :
                                            item.name === "Afiliados" ? isDispatchersOpen :
                                            isUserMgmtOpen;
                                         const toggleGroup = (e: React.MouseEvent<HTMLDivElement>) => {
                                             if (isCollapsed) {
                                                 setIsCollapsed(false);
                                                 localStorage.setItem("adminSidebarCollapsed", "false");
                                                 if (item.name === "Reportes") {
                                                     setIsReportsOpen(true);
                                                 } else if (item.name === "Afiliados") {
                                                     setIsDispatchersOpen(true);
                                                 } else {
                                                     setIsUserMgmtOpen(true);
                                                 }
                                             } else {
                                                 if (item.name === "Reportes") {
                                                     setIsReportsOpen(!isReportsOpen);
                                                 } else if (item.name === "Afiliados") {
                                                     setIsDispatchersOpen(!isDispatchersOpen);
                                                 } else {
                                                     setIsUserMgmtOpen(!isUserMgmtOpen);
                                                 }
                                             }

                                             // Smooth scroll to the toggled group element
                                             const target = e.currentTarget;
                                             setTimeout(() => {
                                                 target.scrollIntoView({ behavior: "smooth", block: "center" });
                                             }, 350);
                                         };
                                        return (
                                            <>
                                                <div
                                                    className={`sidebar-link group-toggle ${isGroupOpen ? "open" : ""}`}
                                                    onClick={toggleGroup}
                                                >
                                                    <FontAwesomeIcon icon={item.icon} className="link-icon" />
                                                    <span>{item.name}</span>
                                                    <FontAwesomeIcon
                                                        icon={faChevronLeft}
                                                        className={`group-arrow ${isGroupOpen ? "rotated" : ""}`}
                                                    />
                                                </div>
                                                <ul className={`sidebar-sub-nav ${isGroupOpen ? "expanded" : ""}`}>
                                                    {item.children?.map((child) => (
                                                        <li key={child.path || child.name}>
                                                    <Link
                                                    to={child.path || "#"}
                                                    className={`sidebar-link sub-link ${location.pathname === child.path ? "active" : ""}`}
                                                    onClick={(e) => {
                                                        // 1. Evitamos que el clic active el toggle del grupo padre
                                                        e.stopPropagation(); 
                                                        
                                                        // 2. Cerramos el móvil solo si es necesario
                                                        if (window.innerWidth <= 768) {
                                                        setIsMobileOpen(false);
                                                        }
                                                    }}
                                                    >
                                                    <FontAwesomeIcon icon={child.icon} className="link-icon" />
                                                    <span>{child.name}</span>
                                                    </Link>
                                                </li>
                                                    ))}
                                                </ul>
                                            </>
                                        );
                                    })()
                                ) : (
                                    <Link
                                        to={item.path || "#"}
                                        className={`sidebar-link ${location.pathname === item.path ? "active" : ""}`}
                                        onClick={(e) => {
                                            setIsMobileOpen(false);
                                            if (item.name === "Salir") {
                                                e.preventDefault();
                                                handleLogout();
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={item.icon} className="link-icon" />
                                        <span>{item.name}</span>
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </aside>

                <div
                    className={`sidebar-overlay ${isMobileOpen ? "visible" : ""}`}
                    onClick={() => setIsMobileOpen(false)}
                ></div>

                <div className="dashboard-content">
                    <main className="dashboard-main">{children}</main>
                </div>
            </div>
            <Footer hideGrid={true} />
        </>
    );
};

export default AdminDashboardLayout;
